import { supabase } from '../../shared/supabase/supabaseClient';
import { captureOpsFailure } from '../../shared/sentry/sentryOps';
import {
  CUSTOMER_MOVEMENT_ATTACHMENTS_BUCKET,
  buildCustomerMovementAttachmentStoragePath,
} from './customerMovementAttachmentPaths';

export type CustomerMovementAttachmentRow = {
  id: string;
  movementId: string;
  fileName: string;
  fileUrl: string | null;
  mimeType: string;
  fileSize: number;
  label: string | null;
  createdAt: string;
  createdBy: string | null;
};

const BUCKET = CUSTOMER_MOVEMENT_ATTACHMENTS_BUCKET;

function decodeBase64(b64: string): Uint8Array {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/** Sube el archivo al bucket privado y lo registra vía RPC idempotente. */
export async function saveCustomerMovementAttachment(params: {
  attachmentId: string;
  movementId: string;
  orgId: string;
  fileName: string;
  mimeType: string;
  fileSize: number;
  fileBase64: string;
  label?: string | null;
  createdBy?: string | null;
}): Promise<string> {
  const { attachmentId, movementId, orgId, fileName, mimeType, fileSize, fileBase64, createdBy } = params;

  if (!attachmentId || !orgId || !movementId || !fileBase64) {
    throw new Error('Faltan datos para adjuntar el comprobante.');
  }

  const createdAt = new Date().toISOString();
  const storagePath = buildCustomerMovementAttachmentStoragePath({
    orgId,
    movementId,
    attachmentId,
    mimeType,
  });

  // 1. Subir el archivo al bucket privado (RLS scopea por orgId en el path).
  try {
    const { error: uploadError } = await supabase.storage
      .from(BUCKET)
      .upload(storagePath, decodeBase64(fileBase64), {
        contentType: mimeType,
        upsert: true,
      });
    if (uploadError) {
      throw new Error(`No pude subir el comprobante: ${uploadError.message}`);
    }
  } catch (err) {
    if (err instanceof Error && err.message.startsWith('No pude subir el comprobante')) throw err;
    const message = err instanceof Error ? err.message : String(err);
    captureOpsFailure({
      module: 'customers',
      op: 'customers.movement_attachment.upload',
      error: err instanceof Error ? err : new Error(message),
      path: 'storage',
      itemId: movementId,
      extra: { movementId, attachmentId },
    });
    throw new Error(`No pude subir el comprobante: ${message}`);
  }

  // 2. Registrar el attachment via RPC (valida branch + movimiento server-side).
  const { error: rpcError } = await supabase.schema('customers').rpc('apply_customer_movement_attachment_command', {
    p_operation: 'insert',
    p_payload: {
      attachmentId,
      movementId,
      fileName,
      fileUrl: storagePath,
      mimeType,
      fileSize,
      label: params.label ?? null,
      createdAt,
      createdBy: createdBy ?? '',
      operationId: `attach-${attachmentId}`,
    },
  });

  if (rpcError) {
    // El archivo quedó en storage pero el registro RPC falló — best-effort cleanup.
    try {
      await supabase.storage.from(BUCKET).remove([storagePath]);
    } catch {
      // ignore cleanup failure
    }
    captureOpsFailure({
      module: 'customers',
      op: 'customers.movement_attachment.rpc',
      error: new Error(rpcError.message),
      path: 'rpc',
      itemId: movementId,
      extra: { movementId, attachmentId },
    });
    throw new Error(`No pude registrar el comprobante: ${rpcError.message}`);
  }

  return attachmentId;
}

export async function listCustomerMovementAttachments(movementId: string): Promise<CustomerMovementAttachmentRow[]> {
  const { data, error } = await supabase
    .schema('customers')
    .from('customer_movement_attachments')
    .select('id, movement_id, file_name, file_url, mime_type, file_size, label, created_at, created_by')
    .eq('movement_id', movementId)
    .order('created_at', { ascending: true });

  if (error) {
    captureOpsFailure({
      module: 'customers',
      op: 'customers.movement_attachment.list',
      error: new Error(error.message),
      path: 'select',
      itemId: movementId,
    });
    return [];
  }

  return (data ?? []).map((row) => ({
    id: String(row.id),
    movementId: String(row.movement_id),
    fileName: String(row.file_name),
    fileUrl: row.file_url != null ? String(row.file_url) : null,
    mimeType: String(row.mime_type ?? 'application/octet-stream'),
    fileSize: Number(row.file_size ?? 0),
    label: row.label != null ? String(row.label) : null,
    createdAt: String(row.created_at ?? ''),
    createdBy: row.created_by != null ? String(row.created_by) : null,
  }));
}

export async function deleteCustomerMovementAttachment(attachmentId: string): Promise<void> {
  const { data: row } = await supabase
    .schema('customers')
    .from('customer_movement_attachments')
    .select('file_url')
    .eq('id', attachmentId)
    .maybeSingle();

  const { error: rpcError } = await supabase.schema('customers').rpc('apply_customer_movement_attachment_command', {
    p_operation: 'delete',
    p_payload: { attachmentId, operationId: `delete-${attachmentId}` },
  });

  if (rpcError) {
    throw new Error(`No pude borrar el comprobante: ${rpcError.message}`);
  }

  if (row?.file_url) {
    try {
      await supabase.storage.from(BUCKET).remove([String(row.file_url)]);
    } catch {
      // best-effort: el registro ya se borró via RPC
    }
  }
}

/** Signed URL de corta duración para abrir el comprobante (bucket privado). */
export async function createCustomerMovementAttachmentUrl(fileUrl: string): Promise<string | null> {
  const { data, error } = await supabase.storage.from(BUCKET).createSignedUrl(fileUrl, 300);
  if (error || !data?.signedUrl) return null;
  return data.signedUrl;
}
